import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormInputText } from 'src/components/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import CREATE_REPORT_TEMPLATE from '../../graphql/mutation/createReportTemplate';
import { useMutation } from '@apollo/client';
import {
  convertTimeRangeToTs,
  isRelativeTimeRange,
} from 'src/features/standardDesign/components/DateTimeRangePicker';
import { useDispatch } from 'src/store';
import { setSnackbar } from 'src/slices/components';
import GET_REPORT_TEMPLATES from '../../graphql/queries/getReportTemplates';
import { useNavigate } from 'react-router-dom';
import { IFormSubmitReportCreation } from './FormFirstStepReportCreation';
import CREATE_REPORT_TEMPLATE_AND_TRIGGER from '../../graphql/mutation/createReportTemplateAndTrigger';
import { useLingui } from '@lingui/react';
import { Trans, msg, t } from '@lingui/macro';

interface IForm {
  name: string;
  description: string;
}

const formSchema = z.object({
  name: z.string().min(1, t`El nombre de la plantilla no puede estar vacío`),
  description: z.string().nullable(),
});

function getInputMutation(formReportCreation: IFormSubmitReportCreation, data: IForm) {
  const {
    emails,
    createExcel,
    createPdf,
    timezone,
    date,
    request: { deviceIds, metrics },
    meanWindow,
  } = formReportCreation;

  let relativeDate: string | null = null;
  let startTs: number | null = null;
  let stopTs: number | null = null;

  const isRelative = isRelativeTimeRange(date);
  if (isRelative) {
    relativeDate = date.start;
  } else {
    const { start, stop } = convertTimeRangeToTs(date);
    startTs = start;
    stopTs = stop;
  }

  return {
    name: data.name,
    emails: emails,
    timezone: timezone,
    deviceIds: deviceIds,
    metrics: metrics,
    createPdf: createPdf,
    createExcel: createExcel,
    startTs: startTs,
    stopTs: stopTs,
    relativeDate: relativeDate,
    meanWindow: meanWindow.window,
    description: data.description,
  };
}

const CreateReportTemplateDialog = (props: {
  open: boolean;
  formReportCreation: IFormSubmitReportCreation | undefined;
  onClose: () => void;
}) => {
  const { open, onClose, formReportCreation } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { _ } = useLingui();
  const [mutCreateReportTemplateAndTrigger, { loading: loadingTrigger }] = useMutation(
    CREATE_REPORT_TEMPLATE_AND_TRIGGER,
    {
      refetchQueries: [{ query: GET_REPORT_TEMPLATES }],
      onCompleted: () => {
        onClose();
        dispatch(
          setSnackbar({
            open: true,
            message: _(
              msg`La plantilla ha sido creada correctamente y su reporte será enviado por correo electrónico en breve`,
            ),
            severity: 'success',
          }),
        );
        navigate('/reports');
      },
      onError: (error) => {
        console.error(error);
        dispatch(
          setSnackbar({
            open: true,
            message: _(msg`Error al crear la plantilla`),
            severity: 'error',
          }),
        );
      },
    },
  );

  const [mutCreateReportTemplate, { loading }] = useMutation(CREATE_REPORT_TEMPLATE, {
    refetchQueries: [{ query: GET_REPORT_TEMPLATES }],
    onCompleted: () => {
      onClose();
      dispatch(
        setSnackbar({
          open: true,
          message: _(msg`La plantilla ha sido creada correctamente`),
          severity: 'success',
        }),
      );
      navigate('/reports');
    },
    onError: (error) => {
      console.error(error);
      dispatch(
        setSnackbar({
          open: true,
          message: _(msg`Error al crear la plantilla`),
          severity: 'error',
        }),
      );
    },
  });
  const { control, handleSubmit, formState } = useForm<IForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
    },
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<IForm> = (data) => {
    if (!formReportCreation) {
      console.error('confirmDialogParams is undefined');
      return;
    }
    const input = getInputMutation(formReportCreation, data);
    mutCreateReportTemplate({ variables: { input } });
  };

  const onSubmitTrigger: SubmitHandler<IForm> = (data) => {
    if (!formReportCreation) {
      console.error('confirmDialogParams is undefined');
      return;
    }
    const input = getInputMutation(formReportCreation, data);
    mutCreateReportTemplateAndTrigger({ variables: { input } });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
          <Typography variant="h6">
            <Trans>Crear Plantilla</Trans>
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <FormInputText
            name="name"
            control={control}
            label={_(msg`Nombre de plantilla`)}
          />
          <FormInputText
            name="description"
            control={control}
            multiline
            minRows={3}
            label={_(msg`Descripción`)}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button color="info" variant="outlined" onClick={onClose}>
          <Trans>Cancelar</Trans>
        </Button>
        <Box flex={1} />
        <Button
          disabled={!formState.isValid || loading || loadingTrigger}
          color="success"
          variant="outlined"
          onClick={handleSubmit(onSubmit)}
        >
          <Trans>Guardar</Trans>
        </Button>
        <Button
          disabled={!formState.isValid || loading || loadingTrigger}
          color="success"
          variant="contained"
          onClick={handleSubmit(onSubmitTrigger)}
        >
          <Trans>Guardar y Crear Reporte</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export { CreateReportTemplateDialog };
